/**
 * TaskPicker — searchable task link for the work-report form (Slice C1).
 *
 * A thin domain wrapper over ``AsyncCombobox``: it owns only the task
 * vocabulary (status labels, due-date phrasing, hydration by id) and
 * delegates every picker mechanic — debounce, paging, stale-response
 * protection, keyboard, theming — to the shared base.
 *
 * Replaces a native <select> that was filled with
 * ``fetchAdminTasks({limit: 200})``: past 200 tasks the rest were
 * silently unreachable, and an edit form could not show a linked task
 * that fell outside that first page.
 *
 * Server contract used here:
 *   GET /api/admin/tasks?q=…&sort=picker_relevance&limit=…&offset=…
 *     - ``q``    case-insensitive title substring
 *     - ``sort`` open tasks first, then done, then cancelled; by due
 *                date inside each bucket (overdue floats to the top)
 *   GET /api/admin/tasks/{id}   for hydrating the current selection
 *
 * The payload it feeds back is unchanged: a bare ``task_id`` string.
 */
import React from "react";

import AsyncCombobox from "../../../../components/ui/AsyncCombobox";
import { fetchAdminTaskById, fetchAdminTasks } from "../../../../api/adminData";
import {
  formatDateOnly,
  instantToBusinessDate,
  todayBusinessDate,
} from "../../../../utils/datetime";

const PAGE_SIZE = 20;

const STATUS_LABEL = {
  todo: "Offen",
  in_progress: "In Arbeit",
  blocked: "Blockiert",
  done: "Erledigt",
  cancelled: "Abgebrochen",
};

/**
 * Human due-date phrasing. Overdue and today are called out IN WORDS —
 * colour alone must never be the only signal.
 * Exported for tests.
 */
export function dueLabel(task, now = new Date()) {
  if (!task?.due_at) return "Kein Fälligkeitsdatum";
  const d = new Date(task.due_at);
  if (Number.isNaN(d.getTime())) return "Kein Fälligkeitsdatum";
  const closed = task.status === "done" || task.status === "cancelled";
  const dayStr = instantToBusinessDate(d);
  const todayStr = todayBusinessDate(now);
  if (!closed && dayStr < todayStr) {
    return `Überfällig seit ${formatDateOnly(task.due_at)}`;
  }
  if (!closed && dayStr === todayStr) return "Heute fällig";
  return `Fällig ${formatDateOnly(task.due_at)}`;
}

/** Assignee display: full name is not on the list shape, e-mail is. */
export function assigneeLabel(task) {
  return task?.assigned_to_email || "Nicht zugewiesen";
}

/** Linked entity, if the task carries one. */
export function linkLabel(task) {
  return task?.tenant_display_name || task?.unit_short_id || null;
}

/** Secondary line: "Fällig … · Status · Person · Bezug". Exported so a
 * test can assert the composition without scraping the DOM. */
export function secondaryLine(task, now = new Date()) {
  return [
    dueLabel(task, now),
    STATUS_LABEL[task?.status] || task?.status,
    assigneeLabel(task),
    linkLabel(task),
  ]
    .filter(Boolean)
    .join(" · ");
}

function isClosed(task) {
  return task?.status === "done" || task?.status === "cancelled";
}

export default function TaskPicker({ value, onChange, disabled = false }) {
  const [selected, setSelected] = React.useState(null);

  // Hydrate the CURRENT link by id (edit mode). Without this an already
  // linked task that sorts past page 1 — or is done/cancelled — would
  // render as "nothing selected" and a save would silently unlink it.
  React.useEffect(() => {
    if (!value) {
      setSelected(null);
      return undefined;
    }
    if (selected && String(selected.id) === String(value)) return undefined;
    let cancelled = false;
    fetchAdminTaskById(value)
      .then((task) => {
        if (!cancelled && task) setSelected(task);
      })
      .catch(() => {
        // A deleted or inaccessible task must not break the form; the
        // trigger keeps showing a placeholder and the link stays intact
        // until the user changes it.
      });
    return () => {
      cancelled = true;
    };
  }, [value, selected]);

  const fetchPage = React.useCallback(
    ({ query, offset, limit }) =>
      fetchAdminTasks({
        q: query || undefined,
        sort: "picker_relevance",
        limit,
        offset,
      }),
    []
  );

  const handleChange = React.useCallback(
    (id, item) => {
      setSelected(item);
      onChange(id);
    },
    [onChange]
  );

  return (
    <AsyncCombobox
      label="Verknüpfte Aufgabe (optional)"
      placeholder="— keine —"
      searchPlaceholder="Aufgabe nach Titel suchen …"
      emptyText="Keine passenden Aufgaben gefunden."
      errorText="Aufgaben konnten nicht geladen werden."
      value={value}
      selectedItem={selected}
      onChange={handleChange}
      fetchPage={fetchPage}
      getItemId={(t) => t.id}
      getItemLabel={(t) => t.title}
      pageSize={PAGE_SIZE}
      disabled={disabled}
      renderItem={(task) => (
        <span className="block min-w-0">
          <span
            title={task.title}
            className={
              "block truncate text-[13px] " +
              (isClosed(task)
                ? "font-normal text-muted-foreground"
                : "font-medium text-foreground")
            }
          >
            {task.title}
          </span>
          {/* The meta line ellipsizes in a narrow popover — the native
              tooltip keeps the tail (assignee, linked object) reachable
              instead of silently cutting it. */}
          <span
            title={secondaryLine(task)}
            className="block truncate text-[11px] text-muted-foreground"
          >
            {secondaryLine(task)}
          </span>
        </span>
      )}
    />
  );
}
